import React, {useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import { ButtonGroup, Card, Spinner } from "reactstrap";
import { VsiBubble, VsiH4, VsiLgText } from "../../../styled/App.styled";
import { useVsiAppContext} from "../../../stores/VsiApp";
import {virtualAlertsDefs} from "../../../utils/gridColumnDefs/virtual/virtualUnits";
import Grid from "../../../components/Grid";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import { getReport, postReport } from "../../../utils/Verbs";
import {useTitleFromComponent} from "../../../utils/hooks";
import AcknowledgeButton from "../../../components/AcknowledgeButton";
import { formatDataWithAcknowledgedAndSeverity } from "../../../utils/Helpers";
import VsiAlert from "../../../components/VsiAlert";

export default function VirtualHealthAlerts({context, title}) {

    useTitleFromComponent(`${context} / ${title}`);
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isAcknowledging, setIsAcknowledging] = useState(false);
    const [colDefs, setColDefs] = useState(virtualAlertsDefs);
    const chartTypes = [
        {value: 'all', label: 'All', route: 'all'},
        {value: 'acknowledged', label: 'Yes', route: 'acknowledged'},
        {value: 'unacknowledged', label: 'No', route: 'unacknowledged'},
    ];
    const chartOptionsList = [
        {value: 'all', label: 'All', route: 'all'},
        {value: 'yellow', label: 'Warning', route: 'warning'},
        {value: 'red', label: 'Alert', route: 'alert'},
    ];
    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

    const type = searchParams.get('type');
    let defaultType = chartTypes.find(({value}) => type === value) || chartTypes[2];
    const [chartType, setChartType] = useState(defaultType);

    const [virtualAlerts, setVirtualAlerts] = useState([]);

    const [defaultVirtualData, setDefaultVirtualData] = useState([]);

    const option = searchParams.get('color');
    let defaultOption = chartOptionsList.find(({value}) => option === value) || chartOptionsList[0];
    const [chartOption, setChartOption] = useState(defaultOption);

    const [selectedAlerts, setSelectedAlerts] = useState([]);
    const [alertCount, setAlertCount] = useState({
        acknowledged: 0,
        unacknowledged: 0,
    });

    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);



    const getRouteForAlerts = async () => {
        try {
            const token = await getAccessTokenSilently();
            const route = `/api/v6/report/virtual_health_detail_proc?client_id=${client.clientId}&period_id=${periodId}`;
            const data = await getReport(route, null, token);
            const dataGrid = data?.data.recordsets;
            if (client.serviceNowInstance !== null && client.serviceNowInstance?.length > 1) {
                dataGrid[0]?.forEach(d => {
                    d['serviceNowInstance'] = client.serviceNowInstance;
                    d['serviceNowUser'] = client.serviceNowUser;
                    d['serviceNowPassword'] = client.serviceNowPassword;
                });
                dataGrid[1]?.forEach(d => {
                    d['serviceNowInstance'] = client.serviceNowInstance;
                    d['serviceNowUser'] = client.serviceNowUser;
                    d['serviceNowPassword'] = client.serviceNowPassword;
                });
            } else {
                const newDefs = [...virtualAlertsDefs];
                newDefs.pop();
                setColDefs(newDefs);
            }
            setDefaultVirtualData([...dataGrid[0], ...dataGrid[1]]);
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            setIsLoading(false);
        }
    };

    const onTypeChange = (type) => {
        setChartType(type);
        searchParams.set('type', type.value);
        setSearchParams(searchParams, {replace: true});
    };

    const onOptionChange = (option) => {
        setChartOption(option);
        searchParams.set('color', option.value);
        setSearchParams(searchParams, {replace: true});
    };

    const onRowSelected = (e) => {
        const alertCount = {
            acknowledged: 0,
            unacknowledged: 0,
        };
        const alerts = [...(e?.api?.getSelectedNodes?.() ?? [])].map(({ data }) => {
            if (!data.acknowledged) {
                alertCount.acknowledged = alertCount.acknowledged + 1;
            } else if (data.acknowledged) {
                alertCount.unacknowledged = alertCount.unacknowledged + 1;
            }
            return data
        });
        setSelectedAlerts(alerts);
        setAlertCount(alertCount);
    }

    const onAcknowledge = async () => {
        if (selectedAlerts.length < 1 || isAcknowledging) return;
        let selectedRows = [...selectedAlerts];
        // Acknowledged > Not
        const route = `/api/v6/report/batch/acknowledge_alerts`;
        const updatedAlerts = {};
        const allAlerts = [];
        for (let selectedRow of selectedRows) {
            const alert = {
                acknowledged: (!selectedRow.acknowledged).toString(),
                context: 'virtual',
                description: encodeURIComponent(selectedRow.description),
                alert: encodeURIComponent(selectedRow.alert),
                client_id: client.clientId,
                device_name: encodeURIComponent(selectedRow.device_name),
                category: encodeURIComponent(selectedRow.category),
                severity: selectedRow.severity,
                user: user.email
            }
            allAlerts.push(alert);
            updatedAlerts[selectedRow.id] = !selectedRow.acknowledged;

        }
        setIsAcknowledging(true);
        try {
            const token = await getAccessTokenSilently();
            await postReport(route, allAlerts, token);
            const newAlerts = [...defaultVirtualData].map(alert => {
            const newAlert = {...alert};
                if (updatedAlerts.hasOwnProperty(alert.id)) {
                    newAlert.acknowledged = updatedAlerts[alert.id];
                }
                return newAlert;
            });
            setVirtualAlerts(newAlerts);
            setDefaultVirtualData(newAlerts);
            setSelectedAlerts([]);
            setIsAcknowledging(false);
            setIsError(false);
            setMessage('Alerts updated!')
        } catch (e) {
            console.error(e);
            setIsError(true);
            setMessage(e.message);
            setIsAcknowledging(false);
        }
    };

    // Runs on load and when period ID is changed
    useEffect(() => {
        setDefaultVirtualData([]);
        getRouteForAlerts();
    }, [client, searchParams.get("period_id")]);

    // Only runs when type/color are changed
    useEffect(() => {
        if (isLoading) {
            return;
        }
        setSelectedAlerts([]);
        setAlertCount({
            acknowledged: 0,
            unacknowledged: 0,
        });
        setVirtualAlerts(formatDataWithAcknowledgedAndSeverity(defaultVirtualData, chartType.value, chartOption.value));
    }, [defaultVirtualData, type, option, isLoading]);

    useEffect(() => {
        let timeout = null;
        if (message) {
            timeout = setTimeout(() => {
                setMessage('');
            }, 3500);
        }
        return () => clearTimeout(timeout);
    }, [message]);
    return (
      <>
          <VsiAlert isOpen={Boolean(message)} color={isError ? 'danger' : 'success'} toggle={() => setMessage('')}>
              {message}
          </VsiAlert>
           <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>{`Virtual Health Alerts`}</VsiH4>
            {isLoading ? <Spinner color={"primary"}/> : (
                <div>
                    <VsiBubble noAutobot>
                        <VsiLgText>
                            {`You can ${chartType.value === 'acknowledged' ? 'un' : ''}acknowledge (${chartType.value === 'acknowledged' ? 'show' : 'hide'}) alerts. Use Shift + Click on the grid below to select multiple alerts at once`}
                        </VsiLgText>
                        <div className={"w-100 p-2 d-flex"}>
                            <VsiLabeledSelect options={chartTypes}
                                              isDisabled={isLoading}
                                              label={'Acknowledged'}
                                              minWidth={150}
                                              onChange={onTypeChange}
                                              defaultValue={chartType}/>
                            <VsiLabeledSelect options={chartOptionsList}
                                              isDisabled={isLoading}
                                              label={'Severity'}
                                              minWidth={200}
                                              maxWidth={500}
                                              onChange={onOptionChange}
                                              defaultValue={chartOption}/>
                            <div className="d-flex align-items-end justify-content-between">
                                <ButtonGroup>
                                   <AcknowledgeButton allowAcknowledge={true}
                                           acknowledgedToggle={type === "acknowledged"}
                                           buttonDisplayOverride={type === 'all' ? `Acknowledge ${alertCount.acknowledged}, Unacknowledge ${alertCount.unacknowledged}` : null}
                                           onAcknowledge={onAcknowledge}
                                           isLoading={isLoading || isAcknowledging}
                                           disable={(isLoading || isAcknowledging) || type === 'all' ? !(alertCount.acknowledged > 0 || alertCount.unacknowledged > 0) : selectedAlerts.length < 1}/>
                                </ButtonGroup>
                            </div>
                        </div>
                    </VsiBubble>
                    <VsiBubble>
                        <Grid
                            data={virtualAlerts}
                            columnDefs={colDefs}
                            isLoading={isAcknowledging || isLoading}
                            gridText={`All ${chartType.value.replace('all', '')} alerts`}
                            onRowSelected={onRowSelected}
                            rowSelection={"multiple"}
                            disable={(isLoading || isAcknowledging) || type === "all"}/>
                    </VsiBubble>
                </div>
            )}
         </Card>
      </>
    );
}
